"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader, Flower2, CircleCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
export default function FloralMembersPortal() {
  return <section data-section-id="222620" id="partner-with-us" className="relative flex min-h-screen items-center justify-center bg-background px-4 py-24 md:px-8">
      <div className="mx-auto w-full max-w-5xl">
        <Card className="overflow-hidden border-none shadow-2xl shadow-black/50">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {}
            <div className="relative hidden bg-muted lg:block">
              <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/birkalla_stadium_partnership.png)] bg-cover bg-center" />
              <div className="absolute inset-0 bg-primary/20 mix-blend-multiply" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
              <div className="absolute bottom-12 left-12 right-12 text-primary-foreground">
                <Flower2 className="mb-6 size-12 text-primary opacity-90" />
                <h3 className="font-default mb-2 text-3xl font-extrabold uppercase tracking-tighter text-foreground">
                  Join the Network
                </h3>
                <p className="font-default text-foreground/90">
                  Connect your brand with WT Birkalla's elite community and drive growth through strategic sports partnership.
                </p>
              </div>
            </div>

            {}
            <div className="flex flex-col justify-center bg-card p-8 md:p-12 lg:p-16">
              <div className="mb-8">
                <h2 className="font-default mb-3 text-3xl font-extrabold uppercase tracking-tighter text-primary">
                  Partner With Us
                </h2>
                <p className="font-default text-muted-foreground">
                  Submit your details to join the WT Birkalla Business Network and explore sponsorship opportunities.
                </p>
              </div>

              <FormProvider formKey="partner_form_business_hub" formId="partner_form_business_hub" sectionName="FloralMembersPortal" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              address: "",
              accept_terms: false
            }} validationRules={{
              full_name: {
                required: "Business name is required"
              },
              email: {
                required: "Contact email is required",
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              accept_terms: {
                required: "You must accept the partnership terms"
              }
            }} submitText="Submit Application" successMessage="Application received. Our partnership team will contact you shortly.">
                <RegistrationForm />
              </FormProvider>
            </div>
          </div>
        </Card>
      </div>
    </section>;
}
function RegistrationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-none border border-primary/50 bg-primary/5 p-8 text-center animate-in fade-in zoom-in duration-500">
        <div className="rounded-full bg-primary/10 p-3">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="font-default text-xl font-bold uppercase tracking-tight text-foreground">Success</h3>
        <p className="font-default text-muted-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default uppercase tracking-wider text-xs font-bold text-primary">Business Name</FormLabel>
            <FormControl>
              <Input className="rounded-none border-0 border-b border-secondary focus-visible:border-primary bg-transparent px-0" placeholder="Enter company name" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] uppercase font-mono">
              Official registered business name.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default uppercase tracking-wider text-xs font-bold text-primary">Contact Person & Email</FormLabel>
            <FormControl>
              <Input className="rounded-none border-0 border-b border-secondary focus-visible:border-primary bg-transparent px-0" type="email" placeholder="name@business.com" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] uppercase font-mono">
              Primary contact for partnership discussions.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default uppercase tracking-wider text-xs font-bold text-primary">Interest Level <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <Input className="rounded-none border-0 border-b border-secondary focus-visible:border-primary bg-transparent px-0" placeholder="e.g. Gold Sponsor, Junior Academy, Signage" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] uppercase font-mono">
              Specify which programs interest you most.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="address" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default uppercase tracking-wider text-xs font-bold text-primary">Business Message <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <textarea className="flex min-h-[100px] w-full resize-y rounded-none border-0 border-b border-secondary bg-transparent px-0 py-2 text-base shadow-none placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" placeholder="Tell us about your business goals..." {...field} />
            </FormControl>
            <FormDescription className="text-[10px] uppercase font-mono">
              Briefly describe your interest in WT Birkalla.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="accept_terms" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-none border border-secondary/30 p-4 shadow-none">
            <FormControl>
              <input type="checkbox" className="mt-1 size-4 shrink-0 rounded-none border-primary text-primary focus:ring-primary/50 focus-visible:outline-none" checked={value} onChange={e => onChange(e.target.checked)} {...field} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="cursor-pointer font-default text-xs uppercase font-bold">
                I agree to the Partnership Terms
              </FormLabel>
              <FormDescription className="text-[10px] uppercase font-mono">
                Required to process your application.
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="rounded-none border border-destructive/50 bg-destructive/10 p-3 text-xs font-mono text-destructive uppercase">
          {submitError}
        </div>}

      <Button type="submit" className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-default font-extrabold uppercase tracking-widest" size="lg" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing...
          </> : "Submit Application"}
      </Button>
    </Form>;
}